import { type VariantProps, cva } from 'class-variance-authority'
import React, { ForwardedRef, forwardRef } from 'react'
import { Link } from 'react-router-dom'
import { twMerge } from 'tailwind-merge'

import { Icon, IconNameAndCategoryString } from '../../Icon'
import { Spinner } from '../../Spinner'

const buttonVariants = cva(
    'inline-flex items-center justify-center font-bold \
    focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-info-400 \
    disabled:text-gray-400 disabled:pointer-events-none',
    {
        variants: {
            shape: {
                rect: 'rounded-md before:rounded-md',
                pill: 'rounded-full before:rounded-full',
                square: 'rounded-md aspect-square before:rounded-md',
                circle: 'rounded-full aspect-square before:rounded-full',
            },
            size: {
                small: 'text-sm h-9 px-3 before:-left-3 before:-right-3',
                medium: 'text-base h-12 px-4 before:-left-4 before:-right-4',
            },
            intent: {
                default:
                    'bg-white text-gray-600 border border-gray-300 \
                    disabled:border-gray-200 \
                    hover:bg-linear-to-b hover:from-black/4 hover:to-black/4 hover:text-gray-600 \
                    active:bg-linear-to-b active:from-black/8 active:to-black/8 active:bg-white',
                primary:
                    'bg-primary-700 text-white \
                    disabled:bg-gray-200 \
                    hover:bg-linear-to-b hover:from-white/10 hover:to-white/10 hover:text-white \
                    active:bg-linear-to-b active:from-white/20 active:to-white/20 active:bg-primary-700',
                secondary:
                    'bg-white text-primary-700 border border-gray-300 \
                    disabled:border-gray-200 \
                    hover:bg-linear-to-b hover:from-black/4 hover:to-black/4 hover:text-primary-700 \
                    active:bg-linear-to-b active:from-black/8 active:to-black/8 active:bg-white',
                tertiary:
                    'relative bg-transparent text-primary-700 underline \
                    hover:text-primary-700 active:text-primary-700 px-0 \
                    before:absolute before:top-0 before:bottom-0 \
                    hover:before:bg-black/4 active:before:bg-black/8 \
                    focus-visible:ring-0 focus-visible:before:outline-hidden focus-visible:before:ring-2 focus-visible:before:ring-info-400',
                // The old `tertiary` button style included the background of the button as part of the content
                // of the button. However, in most (if not all) cases we wanted to lay the button out as if the
                // background was _not_ part of the content. The new `tertiary` button style does this and should
                // be used going forward instead of tertiary_deprecated.
                tertiary_deprecated:
                    'bg-transparent text-primary-700 underline \
                    hover:bg-linear-to-b from-black/4 to-black/4 hover:text-primary-700 \
                    active:bg-linear-to-b active:from-black/8 active:to-black/8 active:bg-transparent',
                tertiaryLink:
                    'bg-transparent text-primary-700 underline h-fit \
                    hover:opacity-60 active:opacity-60 hover:text-primary-700',
                destructive:
                    'bg-danger-400 text-white \
                    disabled:bg-gray-200 \
                    hover:bg-gradient-to-b hover:from-white/15 hover:to-white/15 hover:bg-danger-400 hover:text-white \
                    active:bg-gradient-to-b active:from-white/30 active:to-white/30 active:bg-danger-400',
                secondaryDestructive:
                    'bg-white text-danger-400 border border-danger-400 \
                    disabled:bg-gray-200 \
                    hover:bg-gradient-to-b hover:from-white/15 hover:to-white/15 hover:bg-danger-400 hover:text-white \
                    active:bg-gradient-to-b active:from-white/30 active:to-white/30 active:bg-danger-400',
            },
            state: {
                default: '',
                active: '',
                dark: 'hover:from-white/12 hover:to-white/12 text-white hover:bg-gray-700 hover:bg-gradient-to-b hover:text-white \
                 active:bg-gray-700 active:bg-gradient-to-b active:from-white/20 active:to-white/20',
            },
        },
        compoundVariants: [
            {
                shape: 'pill',
                state: 'active',
                class: 'bg-secondary-100 text-secondary-400 border-secondary-400 border-2',
            },
            {
                shape: 'rect',
                size: 'small',
                state: 'active',
                class: 'bg-linear-to-b from-black/8 to-black/8',
            },
            {
                shape: 'square',
                size: 'small',
                class: 'px-2',
            },
            {
                shape: 'square',
                size: 'medium',
                class: 'px-3',
            },
            {
                shape: 'square',
                intent: 'tertiary',
                class: 'before:inset-0',
            },
            {
                shape: 'circle',
                size: 'small',
                class: 'px-2',
            },
            {
                shape: 'circle',
                size: 'medium',
                class: 'px-3',
            },
            {
                shape: 'circle',
                intent: 'tertiary',
                class: 'before:inset-0',
            },
        ],
        defaultVariants: {
            shape: 'rect',
            size: 'medium',
            intent: 'default',
            state: 'default',
        },
    }
)

type ButtonProps = VariantProps<typeof buttonVariants> & {
    leftIcon?: IconNameAndCategoryString | JSX.Element
    rightIcon?: IconNameAndCategoryString | JSX.Element
    isLoading?: boolean
    active?: boolean
    isDense?: boolean
    buttonClassName?: string
} & (
        | ({ href: string; replace?: boolean } & Omit<React.ComponentPropsWithoutRef<'a'>, 'href'>)
        | React.ComponentPropsWithoutRef<'button'>
    )

const Button = forwardRef(
    (
        {
            children,
            className,
            shape,
            size,
            intent,
            state,
            leftIcon,
            rightIcon,
            isLoading,
            active,
            isDense,
            buttonClassName,
            ...props
        }: ButtonProps,
        ref: ForwardedRef<HTMLButtonElement | HTMLAnchorElement>
    ) => {
        const iconSize = size === 'small' ? 'size-4' : 'size-5'

        const buttonBody = (
            <span
                className={twMerge(
                    'flex items-center',
                    isDense ? 'gap-1' : 'gap-2',
                    buttonClassName
                )}
            >
                {!isLoading &&
                    leftIcon &&
                    shape !== 'square' &&
                    shape !== 'circle' &&
                    (typeof leftIcon === 'string' ? (
                        <Icon icon={leftIcon} shouldInheritColor />
                    ) : (
                        leftIcon
                    ))}
                {children}
                {!isLoading &&
                    rightIcon &&
                    shape !== 'square' &&
                    shape !== 'circle' &&
                    (typeof rightIcon === 'string' ? (
                        <Icon icon={rightIcon} shouldInheritColor />
                    ) : (
                        rightIcon
                    ))}
                {isLoading && <Spinner variant="thick" className={iconSize} />}
            </span>
        )

        if ('href' in props) {
            const { href, replace, ...rest } = props
            return (
                <Link
                    to={href}
                    {...rest}
                    replace={replace}
                    ref={ref as ForwardedRef<HTMLAnchorElement>}
                    className={twMerge(
                        buttonVariants({
                            shape,
                            size,
                            intent,
                            state: active ? 'active' : state,
                            className,
                        })
                    )}
                >
                    {buttonBody}
                </Link>
            )
        }

        const { disabled, ...rest } = props
        return (
            <button
                {...rest}
                ref={ref as ForwardedRef<HTMLButtonElement>}
                className={twMerge(
                    buttonVariants({
                        shape,
                        size,
                        intent,
                        state: active ? 'active' : state,
                        className,
                    })
                )}
                disabled={disabled || isLoading}
            >
                {buttonBody}
            </button>
        )
    }
)
Button.displayName = 'Button'

export default Button
